import React, { useMemo } from 'react';
import { cn, formatINR, formatPercent, calculateStopLossBufferPercent, calculateTargetGapPercent, calculateRangePosition } from '@/lib/utils';
import { ShieldAlert, Target } from 'lucide-react';

export interface PositionRangeBarProps {
  entryPrice: number;
  ltp: number | null | undefined;
  stopLoss: number | null | undefined;
  takeProfit: number | null | undefined;
  compact?: boolean;
  className?: string;
}

export const PositionRangeBar: React.FC<PositionRangeBarProps> = ({
  entryPrice,
  ltp,
  stopLoss,
  takeProfit,
  compact = false,
  className,
}) => {
  const isLtpValid = ltp !== null && ltp !== undefined && !isNaN(ltp) && isFinite(ltp) && ltp > 0;
  const isEntryValid = !isNaN(entryPrice) && isFinite(entryPrice) && entryPrice > 0;
  const isSlValid = stopLoss !== null && stopLoss !== undefined && !isNaN(stopLoss) && isFinite(stopLoss) && stopLoss > 0;
  const isTpValid = takeProfit !== null && takeProfit !== undefined && !isNaN(takeProfit) && isFinite(takeProfit) && takeProfit > 0;

  // Calculate percentages
  const slBuffer = useMemo(() => calculateStopLossBufferPercent(ltp, stopLoss), [ltp, stopLoss]);
  const targetGap = useMemo(() => calculateTargetGapPercent(ltp, takeProfit), [ltp, takeProfit]);

  // Determine domain bounds
  const { entryPos, ltpPos, hasRange } = useMemo(() => {
    const validPrices: number[] = [];
    if (isEntryValid) validPrices.push(entryPrice);
    if (isLtpValid && ltp !== null && ltp !== undefined) validPrices.push(ltp);
    if (isSlValid && stopLoss !== null && stopLoss !== undefined) validPrices.push(stopLoss);
    if (isTpValid && takeProfit !== null && takeProfit !== undefined) validPrices.push(takeProfit);

    if (validPrices.length < 2) {
      return { entryPos: 50, ltpPos: 50, hasRange: false };
    }

    const min = Math.min(...validPrices);
    const max = Math.max(...validPrices);

    if (max <= min) {
      return { entryPos: 50, ltpPos: 50, hasRange: false };
    }

    const ePos = isEntryValid ? calculateRangePosition(entryPrice, min, max) : 50;
    const lPos = isLtpValid && ltp !== null && ltp !== undefined ? calculateRangePosition(ltp, min, max) : 50;

    return { entryPos: ePos, ltpPos: lPos, hasRange: true };
  }, [entryPrice, ltp, stopLoss, takeProfit, isEntryValid, isLtpValid, isSlValid, isTpValid]);

  const isProfit = isLtpValid && isEntryValid && ltp !== null && ltp !== undefined && ltp >= entryPrice;
  const isLoss = isLtpValid && isEntryValid && ltp !== null && ltp !== undefined && ltp < entryPrice;
  const isSlCrossed = slBuffer !== null && slBuffer < 0;
  const isTpCrossed = targetGap !== null && targetGap < 0;

  // Accessible descriptive label
  const accessibleAriaLabel = `Price Range: Stop Loss ${
    isSlValid && stopLoss ? formatINR(stopLoss) : 'None'
  }, Entry ${formatINR(entryPrice)}, LTP ${
    isLtpValid && ltp ? formatINR(ltp) : 'Pending Cycle'
  }, Take Profit ${isTpValid && takeProfit ? formatINR(takeProfit) : 'None'}. ${
    slBuffer !== null ? `SL Buffer ${slBuffer.toFixed(2)}%.` : ''
  } ${targetGap !== null ? `Target Gap ${targetGap.toFixed(2)}%.` : ''}`;

  return (
    <div
      className={cn('font-mono space-y-1.5 select-none', compact && 'space-y-1', className)}
      role="region"
      aria-label={accessibleAriaLabel}
    >
      {/* Top Row: SL & TP Boundary Targets */}
      <div className="flex items-center justify-between text-[10px] leading-tight text-slate-400">
        <span
          className={cn(
            'flex items-center gap-1 font-medium',
            isSlCrossed ? 'text-rose-400 font-bold' : isSlValid ? 'text-slate-300' : 'text-slate-500'
          )}
        >
          <ShieldAlert className={cn('w-3 h-3 shrink-0', isSlCrossed ? 'text-rose-400' : 'text-rose-500/70')} />
          <span>SL: {isSlValid && stopLoss ? formatINR(stopLoss) : '—'}</span>
        </span>

        <span
          className={cn(
            'flex items-center gap-1 font-medium',
            isTpCrossed ? 'text-emerald-400 font-bold' : isTpValid ? 'text-slate-300' : 'text-slate-500'
          )}
        >
          <Target className={cn('w-3 h-3 shrink-0', isTpCrossed ? 'text-emerald-400' : 'text-emerald-500/70')} />
          <span>TP: {isTpValid && takeProfit ? formatINR(takeProfit) : '—'}</span>
        </span>
      </div>

      {/* Middle Row: Visual Track with Entry Tick and LTP Marker */}
      <div className="relative h-2 w-full bg-slate-800/90 rounded-full border border-slate-700/60 overflow-visible flex items-center">
        {hasRange ? (
          <>
            {/* Zone between Entry and LTP */}
            {isLtpValid && (
              <div
                className={cn(
                  'absolute top-0 bottom-0 rounded-full transition-all duration-300',
                  isProfit ? 'bg-emerald-500/30' : 'bg-rose-500/30'
                )}
                style={{
                  left: `${Math.min(entryPos, ltpPos)}%`,
                  width: `${Math.max(1, Math.abs(ltpPos - entryPos))}%`,
                }}
              />
            )}

            {/* Entry Price Tick Marker */}
            {isEntryValid && (
              <div
                className="absolute -top-1 bottom-0 w-0.5 h-4 bg-slate-300 z-10 shadow-sm"
                style={{ left: `${entryPos}%` }}
                title={`Entry: ${formatINR(entryPrice)}`}
              />
            )}

            {/* LTP Current Price Marker */}
            {isLtpValid ? (
              <div
                className={cn(
                  'absolute -top-1 w-4 h-4 -ml-2 rounded-full border-2 border-slate-950 shadow-md z-20 transition-all duration-300 flex items-center justify-center',
                  isSlCrossed
                    ? 'bg-rose-500 ring-2 ring-rose-400/50 animate-pulse'
                    : isTpCrossed
                    ? 'bg-emerald-400 ring-2 ring-emerald-300/50 animate-pulse'
                    : isProfit
                    ? 'bg-emerald-400 ring-1 ring-emerald-400/40'
                    : isLoss
                    ? 'bg-rose-400 ring-1 ring-rose-400/40'
                    : 'bg-slate-300'
                )}
                style={{ left: `${ltpPos}%` }}
                title={`LTP: ${ltp ? formatINR(ltp) : '—'}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-slate-950/80" />
              </div>
            ) : (
              <div
                className="absolute inset-0 flex items-center justify-center text-[9px] text-amber-400/90 font-mono bg-amber-950/20"
                title="Pending Market Cycle"
              >
                <span>⏳</span>
              </div>
            )}
          </>
        ) : (
          /* Safe neutral fallback track when range cannot be computed */
          <div className="w-full h-full bg-slate-800/50 rounded-full flex items-center justify-center">
            <span className="text-[9px] text-slate-500">Neutral</span>
          </div>
        )}
      </div>

      {/* Bottom Row: SL Buffer & Target Gap Percentages */}
      <div className="flex items-center justify-between text-[10px] leading-tight text-slate-400 pt-0.5">
        <div className="flex items-center gap-1">
          <span className="text-slate-500">Buffer:</span>
          {slBuffer !== null ? (
            <span
              className={cn(
                'font-semibold tabular-nums',
                slBuffer < 0
                  ? 'text-rose-400 font-bold'
                  : slBuffer <= 2
                  ? 'text-amber-400'
                  : 'text-slate-300'
              )}
            >
              {formatPercent(slBuffer, { showSign: true, precision: 2 })}
            </span>
          ) : (
            <span className="text-slate-500">—</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <span className="text-slate-500">Gap:</span>
          {targetGap !== null ? (
            <span
              className={cn(
                'font-semibold tabular-nums',
                targetGap < 0
                  ? 'text-emerald-400 font-bold'
                  : targetGap <= 2
                  ? 'text-emerald-300'
                  : 'text-slate-300'
              )}
            >
              {formatPercent(targetGap, { showSign: true, precision: 2 })}
            </span>
          ) : (
            <span className="text-slate-500">—</span>
          )}
        </div>
      </div>
    </div>
  );
};
